import React from "react";
// import Image from "next/image";
import { motion, AnimatePresence, MotionConfig } from "framer-motion";
import { useIsMobile } from "@/hooks/use-mobile";
import { usePathname } from "next/navigation";
import * as SliderPrimitive from "@radix-ui/react-slider";
import {
  Check,
  Crosshair,
  PanelBottomOpen,
  MoreHorizontal,
  Play,
  UserCircle,
  Plus,
  Lock,
  Mic,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AddToPlaylistDialog } from "@/components/playlist/AddToPlaylistDialog";
import { SpinnerIcon } from "../ui/SpinnerIcon";
import { useI18n } from "@/lib/i18n";
import { FREE_PREVIEW_LOCK_REASON } from "@/contexts/player-context";
import { cn } from "@/lib/utils";
import { useUserSettings } from "@/contexts/user-settings-context";

const MotionButton = motion(Button);

export const BOTTOM_PLAYER_TIME_SLIDER_CONTAINER_ID = 'bottom-player-time-slider-container';
export const BOTTOM_PLAYER_TIME_SLIDER_ELEMENT_ID = 'bottom-player-time-slider';
export const BOTTOM_PLAYER_TIME_SLIDER_CONTAINER_READY_EVENT = 'bottom-player-time-slider-container-ready';
export const BOTTOM_PLAYER_TIME_SLIDER_MOUNTED_EVENT = 'bottom-player-time-slider-mounted';
export const BOTTOM_PLAYER_TIME_SLIDER_UNMOUNTED_EVENT = 'bottom-player-time-slider-unmounted';
export const BOTTOM_PLAYER_VARIANT_CHANGED_EVENT = 'bottom-player-variant-changed';

interface ChapterSegment {
  id: string;
  title: string;
  startTime: number;
  endTime: number;
  startPercentage: number;
  widthPercentage: number;
}

interface BottomPlayerControlsProps {
  content: any;
  youtubeTime: number;
  youtubeDuration: number;
  isPlaying: boolean;
  playbackSpeed: number;
  cleanReadData: any;
  onSkipBackward: () => void;
  onSkipForward: () => void;
  onScrollToCurrentTranscript: () => void;
  onTogglePlayPause: () => void;
  onSetPlaybackSpeed: (speed: number) => void;
  onScrubStart?: () => void;
  onScrubChange?: (time: number) => void;
  onScrubEnd?: (time: number) => void;
  formatTime: (time: number) => string;
  isUserLoading: boolean;
  loadingProgress: { data: boolean };
  onNavigateToEpisode?: () => void;
  isPlaybackLocked?: boolean;
  playbackLockReason?: string | null;
  onOpenFullPlayer?: () => void;
  hasSubtitles?: boolean;
  onShadowingClick?: () => void;
}

// Helper function to calculate chapter segments with spacing
const calculateChapterSegments = (
  breakdown: any[], 
  duration: number
): ChapterSegment[] => {
  if (!breakdown || breakdown.length === 0 || !duration) return [];
  
  const gapPercentage = 0.2; // Small gap between segments
  const totalGapPercentage = gapPercentage * (breakdown.length - 1);
  const availablePercentage = 100 - totalGapPercentage;
  
  return breakdown.map((chapter, index) => {
    const startTime = chapter.timestamp;
    const endTime = index < breakdown.length - 1 
      ? breakdown[index + 1].timestamp 
      : duration;
    
    const segmentDuration = endTime - startTime;
    const segmentPercentage = (segmentDuration / duration) * availablePercentage;
    const gapOffset = index * gapPercentage;
    
    const previousSegmentsPercentage = breakdown
      .slice(0, index)
      .reduce((acc, prevChapter, prevIndex) => {
        const prevEndTime = prevIndex < breakdown.length - 1 
          ? breakdown[prevIndex + 1].timestamp 
          : duration;
        const prevDuration = prevEndTime - prevChapter.timestamp;
        return acc + (prevDuration / duration) * availablePercentage;
      }, 0);
    
    return {
      id: `chapter-${index}`,
      title: chapter.chapterTitle,
      startTime,
      endTime,
      startPercentage: previousSegmentsPercentage + gapOffset,
      widthPercentage: segmentPercentage
    };
  });
};

export const BottomPlayerControls: React.FC<BottomPlayerControlsProps> = ({
  content,
  youtubeTime,
  youtubeDuration,
  isPlaying,
  playbackSpeed,
  cleanReadData,
  onSkipBackward,
  onSkipForward,
  onScrollToCurrentTranscript,
  onTogglePlayPause,
  onSetPlaybackSpeed,
  onScrubStart,
  onScrubChange,
  onScrubEnd,
  formatTime,
  isUserLoading,
  loadingProgress,
  onNavigateToEpisode,
  isPlaybackLocked = false,
  playbackLockReason = null,
  onOpenFullPlayer,
  hasSubtitles = false,
  onShadowingClick,
}) => {
  const { t } = useI18n();
  const { userSettings } = useUserSettings();

  // Get user's target language code
  const userTargetLanguageCode = React.useMemo(() => {
    return userSettings?.languageSetting?.split('-')[0]?.toLowerCase() || 'zh';
  }, [userSettings?.languageSetting]);

  // Helper function to extract title translation string from string or object
  const getTitleTranslationString = React.useCallback((titleTranslation: string | Record<string, unknown> | null | undefined): string => {
    if (!titleTranslation) return "";
    
    // If it's a string, return as-is
    if (typeof titleTranslation === "string") {
      return titleTranslation;
    }
    
    // If it's an object, get the translation for user's target language
    if (typeof titleTranslation === "object" && titleTranslation !== null) {
      // Try user's language first
      const userLangTranslation = titleTranslation[userTargetLanguageCode];
      if (userLangTranslation && typeof userLangTranslation === "string" && userLangTranslation.trim().length > 0) {
        return userLangTranslation;
      }
      
      // Fallback to Chinese if available
      const zhTranslation = titleTranslation["zh"];
      if (zhTranslation && typeof zhTranslation === "string" && zhTranslation.trim().length > 0) {
        return zhTranslation;
      }
      
      // Return first available string translation
      for (const value of Object.values(titleTranslation)) {
        if (typeof value === "string" && value.trim().length > 0) {
          return value;
        }
      }
    }
    
    return "";
  }, [userTargetLanguageCode]);

  // Local state for bottom player expansion
  const [isBottomPlayerExpanded, setIsBottomPlayerExpanded] = React.useState(false);
  const [showAddToPlaylistDialog, setShowAddToPlaylistDialog] = React.useState(false);
  const isMobile = useIsMobile();
  const pathname = usePathname();
  const isOnEpisodePage = pathname.startsWith('/episodes/');
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragValue, setDragValue] = React.useState<number | null>(null);
  const sliderRef = React.useRef<React.ElementRef<typeof SliderPrimitive.Root> | null>(null);
  const [sliderElement, setSliderElement] = React.useState<HTMLElement | null>(null);
  const [isTimeSliderMounted, setIsTimeSliderMounted] = React.useState(false);
  
  // 修复hydration错误：确保服务端和客户端初始状态一致
  const [isClientHydrated, setIsClientHydrated] = React.useState(false);
  
  React.useEffect(() => {
    setIsClientHydrated(true);
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const detail = { expanded: isBottomPlayerExpanded };
    window.dispatchEvent(
      new CustomEvent(BOTTOM_PLAYER_VARIANT_CHANGED_EVENT, { detail }),
    );

    const container = document.getElementById(
      BOTTOM_PLAYER_TIME_SLIDER_CONTAINER_ID,
    );
    if (container) {
      container.setAttribute(
        'data-slider-variant',
        isBottomPlayerExpanded ? 'expanded' : 'collapsed',
      );
    }
  }, [isBottomPlayerExpanded]);
  
  // 🔥 优化：采用 Podverse 风格的简洁处理，移除复杂的phantom检测
  const lastClickTimeRef = React.useRef<number>(0);
  const SIMPLE_DEBOUNCE_MS = 200; // 简化防抖，只防止意外双击
  
  // 🔥 简化的播放/暂停处理 - 参考 Podverse 的直接控制方式
  const handleOptimizedPlayPause = React.useCallback((e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }

    const now = Date.now();
    const timeSinceLastClick = now - lastClickTimeRef.current;

    // console.log('🎮 ⚡ Simple play/pause called:', {
    //   isPlaying,
    //   timeSinceLastClick,
    //   isTrusted: e?.isTrusted
    // });
    
    // 只进行基础的防抖和信任检查
    if (!e?.isTrusted && e?.isTrusted !== undefined) {
      console.log('🎮 BLOCKED: Untrusted event');
      return;
    }
    
    if (timeSinceLastClick < SIMPLE_DEBOUNCE_MS) {
      console.log('🎮 DEBOUNCED: Too quick:', timeSinceLastClick + 'ms');
      return;
    }
    
    lastClickTimeRef.current = now;

    // 🔥 直接调用，不做复杂的状态管理
    onTogglePlayPause();

    if (!isPlaying && isOnEpisodePage) {
      onScrollToCurrentTranscript();
    }
  }, [onTogglePlayPause, onScrollToCurrentTranscript, isPlaying, isOnEpisodePage]);
  
  const [hoveredChapter, setHoveredChapter] = React.useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = React.useState({ x: 0, visible: false });
  const [hoverTime, setHoverTime] = React.useState<{ time: number; percent: number; visible: boolean }>({ time: 0, percent: 0, visible: false });
  
  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const signalReady = () => {
      const container = document.getElementById(BOTTOM_PLAYER_TIME_SLIDER_CONTAINER_ID);
      if (container) {
        window.dispatchEvent(new Event(BOTTOM_PLAYER_TIME_SLIDER_CONTAINER_READY_EVENT));
      }
    };

    signalReady();
    const timer = window.setTimeout(signalReady, 0);
    return () => window.clearTimeout(timer);
  }, [isBottomPlayerExpanded]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const updateSliderElement = () => {
      const el = document.getElementById(BOTTOM_PLAYER_TIME_SLIDER_ELEMENT_ID);
      if (el) {
        setSliderElement(el);
        setIsTimeSliderMounted(true);
      }
    };

    const handleMounted = () => {
      updateSliderElement();
    };

    const handleUnmounted = () => {
      setSliderElement(null);
      setIsTimeSliderMounted(false);
      setIsDragging(false);
      setDragValue(null);
      setHoverTime((prev) => ({ ...prev, visible: false }));
      setTooltipPosition((prev) => (prev.visible ? { ...prev, visible: false } : prev));
    };

    const handleContainerReady = () => {
      updateSliderElement();
    };

    updateSliderElement();
    window.addEventListener(BOTTOM_PLAYER_TIME_SLIDER_CONTAINER_READY_EVENT, handleContainerReady);
    window.addEventListener(BOTTOM_PLAYER_TIME_SLIDER_MOUNTED_EVENT, handleMounted);
    window.addEventListener(BOTTOM_PLAYER_TIME_SLIDER_UNMOUNTED_EVENT, handleUnmounted);

    return () => {
      window.removeEventListener(BOTTOM_PLAYER_TIME_SLIDER_CONTAINER_READY_EVENT, handleContainerReady);
      window.removeEventListener(BOTTOM_PLAYER_TIME_SLIDER_MOUNTED_EVENT, handleMounted);
      window.removeEventListener(BOTTOM_PLAYER_TIME_SLIDER_UNMOUNTED_EVENT, handleUnmounted);
    };
  }, []);

  const isPreviewLocked = Boolean(
    isPlaybackLocked && playbackLockReason === FREE_PREVIEW_LOCK_REASON
  );
  const showLoadingSpinner =
    (isUserLoading || loadingProgress.data) && !isPreviewLocked;
  const isPlayButtonDisabled =
    isUserLoading || loadingProgress.data || isPlaybackLocked;
  const previewLockMessage = t(
    "episode.player.previewLock.message",
    "Free plan preview is limited to the first 2 minutes. Upgrade to continue listening."
  );
  const previewLockBadge = t(
    "episode.player.previewLock.badge",
    "Preview limit"
  );
  const previewLockTooltip = t(
    "episode.player.previewLock.tooltip",
    "Free plan preview is limited to the first 2 minutes. Upgrade to continue listening."
  );
  const playbackLockTitle = isPreviewLocked
    ? previewLockTooltip
    : isPlaybackLocked
    ? playbackLockReason ||
      t("episode.player.previewLock.tooltip", "Playback temporarily locked")
    : undefined;

  // Mobile: add extra bottom offset for bottom navigation (h-16 = 4rem)
  const safeAreaClass = isMobile
    ? "bottom-[calc(4rem+0.2rem)]"
    : "safe-area-bottom-offset sm:safe-area-bottom-offset-lg";

  const containerSpacingClass = isMobile
    ? "left-2 right-2"
    : "left-4 sm:left-72 right-4 sm:right-72";

  const innerWidthClass = isMobile ? "max-w-full" : "max-w-4xl";
  const innerMarginClass = isMobile ? "mx-2" : "mx-2 sm:mx-6";
  const cardWrapperStyle = React.useMemo(
    () =>
      ({
        backgroundColor: isBottomPlayerExpanded
          ? "rgba(255,255,255,0.18)"
          : "rgba(255,255,255,0.15)",
        backdropFilter: isBottomPlayerExpanded
          ? "blur(18px) saturate(2.1)"
          : "blur(16px) saturate(1.8)",
        WebkitBackdropFilter: isBottomPlayerExpanded
          ? "blur(18px) saturate(2.1)"
          : "blur(16px) saturate(1.8)",
        transformOrigin: "center bottom",
        overflow: "hidden",
      }) as React.CSSProperties,
    [isBottomPlayerExpanded],
  );

  const dynamicIslandTransition = React.useMemo(
    () => ({
      layout: {
        type: "spring" as const,
        stiffness: 42,
        damping: 28,
        mass: 1.24,
      },
      y: {
        type: "tween" as const,
        duration: 0.62,
        ease: [0.25, 0.88, 0.35, 1] as const,
        times: [0, 0.45, 1],
      },
      scaleX: {
        type: "tween" as const,
        duration: 0.56,
        ease: [0.22, 0.84, 0.38, 1] as const,
        times: [0, 0.45, 1],
      },
      scaleY: {
        type: "tween" as const,
        duration: 0.56,
        ease: [0.22, 0.84, 0.38, 1] as const,
        times: [0, 0.45, 1],
      },
      borderRadius: {
        type: "tween" as const,
        duration: 0.68,
        ease: [0.3, 0.82, 0.35, 1] as const,
      },
      boxShadow: {
        type: "tween" as const,
        duration: 0.68,
        ease: [0.3, 0.82, 0.35, 1] as const,
      },
    }),
    [],
  );

  const dynamicIslandKeyframes = React.useMemo(
    () =>
      isBottomPlayerExpanded
        ? {
            scaleX: [1, 1.045, 1],
            scaleY: [1, 0.94, 1],
            y: [0, -6, 0],
          }
        : {
            scaleX: [1, 0.96, 1],
            scaleY: [1, 1.06, 1],
            y: [0, 6, 0],
          },
    [isBottomPlayerExpanded],
  );

  const dynamicIslandContentTransition = React.useMemo(
    () => ({
      layout: {
        type: "spring" as const,
        stiffness: 160,
        damping: 26,
        mass: 1,
      },
      maxHeight: {
        type: "tween" as const,
        duration: 0.58,
        ease: [0.24, 0.9, 0.32, 1] as const,
      },
      paddingTop: {
        type: "tween" as const,
        duration: 0.38,
        ease: [0.3, 0.82, 0.35, 1] as const,
      },
      paddingBottom: {
        type: "tween" as const,
        duration: 0.38,
        ease: [0.3, 0.82, 0.35, 1] as const,
      },
    }),
    [],
  );

  const collapsedPadding = React.useMemo(
    () => ({
      top: 0,
      bottom: 4,
    }),
    [],
  );

  const expandedPadding = React.useMemo(
    () => ({
      top: isMobile ? 4 : 6,
      bottom: 8,
    }),
    [isMobile],
  );

  const panelContentTransition = React.useMemo(
    () => ({
      duration: 0.68,
      ease: [0.18, 0.88, 0.36, 1] as const,
    }),
    [],
  );

  const collapsedMeasurementRef = React.useRef<HTMLDivElement | null>(null);
  const [collapsedContentHeight, setCollapsedContentHeight] = React.useState<number>(20);

  React.useLayoutEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    if (isBottomPlayerExpanded) {
      return;
    }

    const node = collapsedMeasurementRef.current;
    if (!node) {
      return;
    }

    const measure = () => {
      const nextHeight = node.scrollHeight;
      if (!Number.isNaN(nextHeight) && nextHeight > 0) {
        setCollapsedContentHeight(nextHeight);
      }
    };

    measure();

    if (typeof (window as any).ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(() => measure());
      observer.observe(node);
      return () => observer.disconnect();
    }

    const handleResize = () => measure();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isBottomPlayerExpanded]);

  const collapsedMaxHeight = React.useMemo(
    () => Math.max(collapsedContentHeight + 6, isMobile ? 28 : 34),
    [collapsedContentHeight, isMobile],
  );

  const expandedMaxHeight = React.useMemo(
    () => (isMobile ? 520 : 620),
    [isMobile],
  );

  // Calculate chapters from cleanReadData
  const chapters = React.useMemo(() => {
    if (cleanReadData?.breakdown && youtubeDuration > 0) {
      return calculateChapterSegments(cleanReadData.breakdown, youtubeDuration);
    }
    return [];
  }, [cleanReadData?.breakdown, youtubeDuration]);

  const safeDuration = React.useMemo(
    () => (Number.isFinite(youtubeDuration) && youtubeDuration > 0 ? youtubeDuration : 0),
    [youtubeDuration]
  );

  const safeTime = React.useMemo(
    () => (Number.isFinite(youtubeTime) && youtubeTime >= 0 ? youtubeTime : 0),
    [youtubeTime]
  );

  const clampTime = React.useCallback(
    (value: number) => Math.max(0, Math.min(value, safeDuration || 0)),
    [safeDuration],
  );

  const findChapterByValue = React.useCallback(
    (value: number | null | undefined) => {
      if (!Array.isArray(chapters) || chapters.length === 0 || typeof value !== 'number') {
        setHoveredChapter(null);
        return null;
      }
      const chapterMatch = chapters.find((chapterEntry, index) => {
        const nextStart = index < chapters.length - 1 ? chapters[index + 1].startTime : Number.POSITIVE_INFINITY;
        return value >= chapterEntry.startTime && value < nextStart;
      });
      setHoveredChapter(chapterMatch ? chapterMatch.id : null);
      return chapterMatch ?? null;
    },
    [chapters],
  );

  const updateHoverFromValue = React.useCallback(
    (value: number, visible = true) => {
      if (isMobile) return;
      const sliderNode =
        sliderElement ??
        sliderRef.current ??
        (typeof document !== 'undefined'
          ? (document.getElementById(
              BOTTOM_PLAYER_TIME_SLIDER_ELEMENT_ID,
            ) as HTMLElement | null)
          : null);
      if (!sliderNode) return;
      const rect = sliderNode.getBoundingClientRect();
      const clampedValue = clampTime(value);
      const ratio = safeDuration > 0 ? clampedValue / safeDuration : 0;
      let px = rect.width * ratio;
      const thumbEl = sliderNode.querySelector('.vds-slider-thumb') as HTMLElement | null;
      if (thumbEl) {
        const thumbRect = thumbEl.getBoundingClientRect();
        px = thumbRect.left + thumbRect.width / 2 - rect.left;
      }
      const clampedPx = Math.max(0, Math.min(rect.width, px));
      const percentage = rect.width > 0 ? (clampedPx / rect.width) * 100 : 0;
      setHoverTime({
        time: clampedValue,
        percent: percentage,
        visible,
      });
      setTooltipPosition({
        x: percentage,
        visible,
      });
      findChapterByValue(clampedValue);
    },
    [clampTime, findChapterByValue, isMobile, safeDuration, sliderElement],
  );

  React.useEffect(() => {
    if (!sliderElement) return;

    const handleDragStart = () => {
      setIsDragging(true);
      onScrubStart?.();
    };

    const handleDragValueChange = (event: Event) => {
      const value = (event as CustomEvent<number>).detail;
      if (typeof value !== 'number') return;
      setDragValue(value);
      onScrubChange?.(value);
      updateHoverFromValue(value, true);
    };

    const handleDragEnd = (event: Event) => {
      const value = (event as CustomEvent<number>).detail;
      setIsDragging(false);
      setDragValue(null);
      if (typeof value === 'number') {
        onScrubEnd?.(value);
        updateHoverFromValue(value, false);
      } else {
        onScrubEnd?.(safeTime);
      }
    };

    const handlePointerValueChange = (event: Event) => {
      if (isDragging) return;
      const value = (event as CustomEvent<number>).detail;
      if (typeof value !== 'number') return;
      updateHoverFromValue(value, false);
    };

    const handlePointerLeave = () => {
      if (!isDragging) {
        setHoverTime((prev) => ({ ...prev, visible: false }));
        setHoveredChapter(null);
      }
    };

    sliderElement.addEventListener('drag-start', handleDragStart as EventListener);
    sliderElement.addEventListener('drag-value-change', handleDragValueChange as EventListener);
    sliderElement.addEventListener('drag-end', handleDragEnd as EventListener);
    sliderElement.addEventListener('pointer-value-change', handlePointerValueChange as EventListener);
    sliderElement.addEventListener('mouseleave', handlePointerLeave);
    sliderElement.addEventListener('pointerleave', handlePointerLeave);

    return () => {
      sliderElement.removeEventListener('drag-start', handleDragStart as EventListener);
      sliderElement.removeEventListener('drag-value-change', handleDragValueChange as EventListener);
      sliderElement.removeEventListener('drag-end', handleDragEnd as EventListener);
      sliderElement.removeEventListener('pointer-value-change', handlePointerValueChange as EventListener);
      sliderElement.removeEventListener('mouseleave', handlePointerLeave);
      sliderElement.removeEventListener('pointerleave', handlePointerLeave);
    };
  }, [
    isDragging,
    onScrubChange,
    onScrubEnd,
    onScrubStart,
    safeTime,
    sliderElement,
    updateHoverFromValue,
  ]);

  const handleChapterJump = React.useCallback((timestamp: number) => {
    onScrubStart?.();
    onScrubChange?.(timestamp);
    onScrubEnd?.(timestamp);
    updateHoverFromValue(timestamp, true);
  }, [onScrubStart, onScrubChange, onScrubEnd, updateHoverFromValue]);

  const effectiveSliderValue = React.useMemo(() => {
    if (isDragging && dragValue !== null) {
      return Math.max(0, Math.min(dragValue, safeDuration));
    }
    return Math.max(0, Math.min(safeTime, safeDuration || safeTime));
  }, [isDragging, dragValue, safeDuration, safeTime]);

  const handleSliderPointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLSpanElement>) => {
      if (event.pointerType === 'mouse' && event.button !== 0) {
        return;
      }
      setIsDragging(true);
      onScrubStart?.();
    },
    [onScrubStart],
  );

  const handleSliderPointerUp = React.useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    setDragValue(null);
  }, [isDragging]);

  const handleSliderValueChange = React.useCallback(
    (values: number[]) => {
      const value = clampTime(values[0] ?? 0);
      if (!isDragging) {
        setIsDragging(true);
        onScrubStart?.();
      }
      setDragValue(value);
      onScrubChange?.(value);
      updateHoverFromValue(value);
    },
    [clampTime, isDragging, onScrubChange, onScrubStart, updateHoverFromValue],
  );

  const handleSliderValueCommit = React.useCallback(
    (values: number[]) => {
      const value = clampTime(values[0] ?? 0);
      setIsDragging(false);
      setDragValue(null);
      onScrubEnd?.(value);
      updateHoverFromValue(value);
    },
    [clampTime, onScrubEnd, updateHoverFromValue],
  );

  const handleSliderPointerMove = React.useCallback(
    (event: React.PointerEvent<HTMLSpanElement>) => {
      if (isMobile || !sliderRef.current) return;
      const rect = sliderRef.current.getBoundingClientRect();
      const relativeX = Math.max(0, Math.min(event.clientX - rect.left, rect.width));
      const value = safeDuration > 0 ? (relativeX / rect.width) * safeDuration : 0;
      const clampedValue = clampTime(value);
      const clampedRatio = safeDuration > 0 ? clampedValue / safeDuration : 0;
      const percent = clampedRatio * 100;
      setHoverTime({
        time: clampedValue,
        percent,
        visible: true,
      });
      setTooltipPosition({ x: percent, visible: true });
    },
    [clampTime, isMobile, safeDuration],
  );

  const handleSliderPointerLeave = React.useCallback(() => {
    if (!isDragging) {
      setHoverTime((prev) => ({ ...prev, visible: false }));
      setTooltipPosition((prev) => ({ ...prev, visible: false }));
    }
  }, [isDragging]);

  // 移除冲突的拖拽处理逻辑，使用外部传入的handlers

  const renderFallbackSlider = React.useCallback(
    (variant: 'collapsed' | 'expanded') => {
      const isCollapsedVariant = variant === 'collapsed';

      const rootClasses = cn(
        'relative flex w-full cursor-pointer select-none',
        isCollapsedVariant ? 'h-[4px] items-center overflow-visible px-0' : 'h-2 items-center',
      );

      const trackClasses = cn(
        'relative w-full grow overflow-hidden',
        isCollapsedVariant ? 'h-[4px] bg-white/60 rounded-t-3xl' : 'rounded-full h-2 bg-foreground/10',
      );

      const thumbClasses = cn(
        'rounded-full opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 group-data-[dragging]:opacity-100',
        isCollapsedVariant
          ? 'relative block h-4 w-4 border border-black/20 bg-black'
          : 'relative block h-3.5 w-3.5 border border-black/30 bg-black shadow-sm',
      );

      return (
        <SliderPrimitive.Root
          ref={sliderRef}
          className={rootClasses}
          min={0}
          max={safeDuration > 0 ? safeDuration : 1}
          step={0.1}
          value={[effectiveSliderValue]}
          disabled={safeDuration <= 0}
          aria-label={t('episode.player.seekSliderLabel', 'Seek')}
          onValueChange={handleSliderValueChange}
          onValueCommit={handleSliderValueCommit}
          onPointerDown={handleSliderPointerDown}
          onPointerUp={handleSliderPointerUp}
          onPointerMove={handleSliderPointerMove}
          onPointerLeave={handleSliderPointerLeave}
          onPointerCancel={handleSliderPointerLeave}
        >
          <SliderPrimitive.Track className={trackClasses}>
            <SliderPrimitive.Range className="absolute h-full bg-black transition-all duration-200" />
          </SliderPrimitive.Track>
          <SliderPrimitive.Thumb className={thumbClasses} />
        </SliderPrimitive.Root>
      );
    },
    [
      effectiveSliderValue,
      handleSliderPointerDown,
      handleSliderPointerLeave,
      handleSliderPointerMove,
      handleSliderPointerUp,
      handleSliderValueChange,
      handleSliderValueCommit,
      safeDuration,
      t,
    ],
  );

  const togglePanel = () => {
    setIsBottomPlayerExpanded(!isBottomPlayerExpanded);
  };

  const handleMainAreaClick = React.useCallback((event?: React.MouseEvent) => {
    const target = event?.target as HTMLElement | null;
    if (target && target.closest('button, a, [data-prevent-main-click="true"]')) {
      return;
    }

    if (onOpenFullPlayer) {
      onOpenFullPlayer();
      return;
    }

    if (!isOnEpisodePage && onNavigateToEpisode) {
      onNavigateToEpisode();
    }
  }, [onOpenFullPlayer, isOnEpisodePage, onNavigateToEpisode]);

  const isMainAreaInteractive = React.useMemo(
    () => Boolean(onOpenFullPlayer || (!isOnEpisodePage && onNavigateToEpisode)),
    [onOpenFullPlayer, isOnEpisodePage, onNavigateToEpisode]
  );

  const expandedPanel = (
    <motion.div
      key="bottom-player-expanded-panel"
      layout={isClientHydrated}
      initial={{ opacity: 0, y: -14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={panelContentTransition}
      className="space-y-2 mb-2"
    >
      <div className="flex items-center justify-between space-x-1 px-2">
        <div className="flex items-center space-x-3 min-w-0">
          <div className="text-sm font-medium text-gray-800 truncate max-w-[55vw] sm:max-w-full">
            {isClientHydrated ? content?.content?.author : ''}
          </div>
          <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
          <div className="text-xs text-gray-600 whitespace-nowrap">
            {isClientHydrated ? `${formatTime(youtubeTime)} / ${formatTime(youtubeDuration)}` : '0:00 / 0:00'}
          </div>
        </div>
        <div className="flex items-center space-x-1">
          <Button
            variant="ghost"
            size="icon"
            className={`h-8 w-8 mr-8 ${!isMobile ? "hover:bg-white/60" : ""} transition-all duration-200 border border-foreground/10`}
            onClick={() => setShowAddToPlaylistDialog(true)}
            title="Add to playlist"
          >
            <Plus className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className={`relative h-8 w-8 rounded-xl ${!isMobile ? "hover:bg-white/60" : ""} transition-all duration-200 border border-foreground/10 ${isBottomPlayerExpanded ? "" : ""}`}
            onClick={onSkipBackward}
          >
            <svg fill="none" height="24" width="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M4.122 13.286c0 4.26 2.96 7.714 7.378 7.714s8-3.058 8-7.714-3.582-7.715-8-7.715h-7m0 0L7.167 3M4.5 5.571l2.667 2.572" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"/>
            </svg>
            <span className="absolute bottom-[6px] left-1/2 -translate-x-1/2 rounded-[2px] border border-current bg-background px-[0.5px] text-[6px] font-semibold leading-[6px]">
              15
            </span>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className={`relative h-8 w-8 rounded-xl ${!isMobile ? "hover:bg-white/60" : ""} transition-all duration-200 border border-foreground/10 ${isBottomPlayerExpanded ? "" : ""}`}
            onClick={onSkipForward}
          >
            <svg fill="none" height="20" width="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M19.878 13.286c0 4.26-2.96 7.714-7.378 7.714s-8-3.058-8-7.714 3.582-7.715 8-7.715h7m0 0L16.833 3M19.5 5.571l-2.667 2.572" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"/>
            </svg>
            <span className="absolute bottom-[6px] left-1/2 -translate-x-1/2 rounded-[2px] border border-current bg-background px-[0.5px] text-[6px] font-semibold leading-[6px]">
              15
            </span>
          </Button>
        </div>
      </div>
      {isPreviewLocked && (
        <div className="rounded-2xl border border-slate-200/80 bg-gradient-to-br from-slate-100/70 via-white to-white px-3 py-2 sm:px-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)] backdrop-blur-sm">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-slate-600">
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-900/90 px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-white shadow-sm">
              <Lock className="h-3 w-3" />
              {previewLockBadge}
            </span>
            <span className="text-xs leading-relaxed sm:text-[13px]">
              {previewLockMessage}
            </span>
          </div>
        </div>
      )}
      <div className="space-y-3">
        {/* Segmented Progress Bar */}
        <div
          className="relative mx-0"
          style={{
            overflow: "visible",
            paddingTop: "1px",
            paddingBottom: "1px",
            marginTop: "-10px",
            marginBottom: "-10px",
            touchAction: "pan-x",
          }}
        >
          <div
            id={BOTTOM_PLAYER_TIME_SLIDER_CONTAINER_ID}
            data-slider-variant="expanded"
            className={cn(
              "group relative inline-flex h-2 w-full select-none items-center justify-center",
            )}
          >
            {!isTimeSliderMounted && renderFallbackSlider('expanded')}
          </div>
          {chapters.length > 0 &&
            chapters.map((chapter) => (
              <motion.div
                key={chapter.id}
                className={cn(
                  "absolute group",
                  hoveredChapter === chapter.id ? "opacity-100" : "opacity-80",
                )}
                style={{
                  left: `${chapter.startPercentage}%`,
                  width: `${chapter.widthPercentage}%`,
                  top: "4px",
                  height: "8px",
                  cursor: "pointer",
                  zIndex: 40,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleChapterJump(chapter.startTime);
                }}
                onMouseEnter={(e) => {
                  if (isMobile) return;
                  setHoveredChapter(chapter.id);
                  const sliderNode =
                    sliderElement ??
                    sliderRef.current ??
                    (document.getElementById(
                      BOTTOM_PLAYER_TIME_SLIDER_ELEMENT_ID,
                    ) as HTMLElement | null) ??
                    (document.getElementById(
                      BOTTOM_PLAYER_TIME_SLIDER_CONTAINER_ID,
                    ) as HTMLElement | null);
                  if (sliderNode) {
                    const rect = sliderNode.getBoundingClientRect();
                    const relativeX = e.clientX - rect.left;
                    let xPercentage = (relativeX / rect.width) * 100;
                    const tooltipWidth = 288;
                    const tooltipHalfWidth = tooltipWidth / 2;
                    const margin = 16;
                    if (relativeX < tooltipHalfWidth + margin) {
                      xPercentage = ((tooltipHalfWidth + margin) / rect.width) * 100;
                    } else if (relativeX > rect.width - tooltipHalfWidth - margin) {
                      xPercentage = ((rect.width - tooltipHalfWidth - margin) / rect.width) * 100;
                    }
                    setTooltipPosition({
                      x: xPercentage,
                      visible: true,
                    });
                  }
                }}
                onMouseLeave={() => {
                  setHoveredChapter(null);
                  setTooltipPosition({ x: 0, visible: false });
                }}
                onTouchStart={(e) => {
                  setHoveredChapter(chapter.id);
                  const sliderNode =
                    sliderElement ??
                    sliderRef.current ??
                    (document.getElementById(
                      BOTTOM_PLAYER_TIME_SLIDER_ELEMENT_ID,
                    ) as HTMLElement | null) ??
                    (document.getElementById(
                      BOTTOM_PLAYER_TIME_SLIDER_CONTAINER_ID,
                    ) as HTMLElement | null);
                  if (sliderNode) {
                    const rect = sliderNode.getBoundingClientRect();
                    const relativeX = e.touches[0].clientX - rect.left;
                    let xPercentage = (relativeX / rect.width) * 100;
                    const tooltipWidth = 288;
                    const tooltipHalfWidth = tooltipWidth / 2;
                    const margin = 16;
                    if (relativeX < tooltipHalfWidth + margin) {
                      xPercentage = ((tooltipHalfWidth + margin) / rect.width) * 100;
                    } else if (relativeX > rect.width - tooltipHalfWidth - margin) {
                      xPercentage = ((rect.width - tooltipHalfWidth - margin) / rect.width) * 100;
                    }
                    setTooltipPosition({
                      x: xPercentage,
                      visible: true,
                    });
                  }
                }}
                onTouchEnd={() => {
                  setHoveredChapter(null);
                  setTooltipPosition({ x: 0, visible: false });
                }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 25,
                }}
              >
                <motion.div
                  className="h-full rounded-full bg-primary/70"
                  whileHover={{
                    backgroundColor: "hsl(var(--primary))",
                  }}
                  transition={{ duration: 0.2 }}
                  style={{ cursor: "pointer" }}
                />
                <div
                  className="absolute inset-0"
                  style={{
                    zIndex: 50,
                    background: "transparent",
                  }}
                />
              </motion.div>
            ))}
        </div>
        {/* Chapter tooltip positioned relative to progress bar */}
        {!isTimeSliderMounted && (
          <>
            <AnimatePresence>
              {hoveredChapter && tooltipPosition.visible && (
                <motion.div
                  className="absolute bottom-full mb-4 pointer-events-none"
                  style={{
                    zIndex: 100,
                    left: `${tooltipPosition.x - 17}%`,
                    transform: 'translateX(10%)',
                    marginBottom: '-12px',
                  }}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 5 }}
                  transition={{ duration: 0.15 }}
                >
                  {(() => {
                    const chapterIndex = chapters.findIndex((ch) => ch.id === hoveredChapter);
                    const chapter = chapters[chapterIndex];
                    if (!chapter) return null;

                    return (
                      <div className="bg-background border rounded-xl p-3 sm:p-4 shadow-xl w-80 max-w-xs sm:w-96 sm:max-w-sm relative">
                        <div
                          className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent"
                          style={{ borderTopColor: 'hsl(var(--background))' }}
                        />
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                          <div className="font-medium text-sm text-foreground truncate">{chapter.title}</div>
                        </div>
                        <div className="text-muted-foreground text-xs mb-3 font-mono">
                          {formatTime(chapter.startTime)} → {formatTime(chapter.endTime)}
                        </div>
                        <div className="text-muted-foreground text-xs leading-relaxed">
                          {cleanReadData?.breakdown?.[chapterIndex]?.content ? (
                            <div>
                              <div className="text-foreground">
                                {cleanReadData.breakdown[chapterIndex].content.length > 120
                                  ? `${cleanReadData.breakdown[chapterIndex].content.substring(0, 120)}...`
                                  : cleanReadData.breakdown[chapterIndex].content}
                              </div>
                            </div>
                          ) : cleanReadData?.breakdown?.[chapterIndex]?.keyPoints && cleanReadData.breakdown[chapterIndex].keyPoints.length > 0 ? (
                            <div>
                              <div className="text-primary text-[10px] uppercase tracking-wide mb-1 font-medium">Key Points</div>
                              <ul className="space-y-1">
                                {cleanReadData.breakdown[chapterIndex].keyPoints.slice(0, 3).map((point: string, i: number) => (
                                  <li key={i} className="flex items-start gap-1.5">
                                    <div className="w-1 h-1 bg-primary rounded-full mt-1.5 flex-shrink-0"></div>
                                    <span className="text-foreground">{point}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : (
                            <div className="text-center">
                              <div className="text-primary text-[10px] uppercase tracking-wide mb-1 font-medium">Navigation</div>
                              <div className="text-muted-foreground">Click to jump to this section</div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {!isMobile && hoverTime.visible && (
                <motion.div
                  className="absolute bottom-full pointer-events-none mb-2"
                  style={{
                    left: `${Math.max(0, Math.min(100, hoverTime.percent))}%`,
                    transform: 'translateX(-50%)',
                    zIndex: 120,
                    marginBottom: '-28px',
                  }}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 5 }}
                  transition={{ duration: 0.15 }}
                >
                  <div className="bg-black/80 text-white text-xs px-2 py-1 rounded-md shadow-lg backdrop-blur-sm relative">
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black/80"></div>
                    {formatTime(hoverTime.time)}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}
      </div>
    </motion.div>
  );

  const collapsedStrip = (
    <motion.div
      key="bottom-player-collapsed-strip"
      layout={isClientHydrated}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={panelContentTransition}
      className="mb-[2px]"
    >
      <div className="relative block h-[4px] w-full select-none">
        <div className="absolute inset-0">
          <div
            id={BOTTOM_PLAYER_TIME_SLIDER_CONTAINER_ID}
            data-slider-variant="collapsed"
            className="group relative block h-full w-full select-none"
          >
            {!isTimeSliderMounted && renderFallbackSlider('collapsed')}
          </div>
        </div>
      </div>
    </motion.div>
  );

  return (
    <MotionConfig
      transition={{
        type: "spring",
        stiffness: isBottomPlayerExpanded ? 120 : 180,
        damping: isBottomPlayerExpanded ? 34 : 42,
      }}
    >
      <>
        <div className={`fixed ${safeAreaClass} ${containerSpacingClass} z-50 flex justify-center`}>
          <motion.div
            layout={isClientHydrated}
            initial={false}
            animate={{
              borderRadius: isBottomPlayerExpanded ? 24 : 18,
              boxShadow: isBottomPlayerExpanded
                ? "0 28px 60px rgba(15,23,42,0.35)"
                : "0 18px 40px rgba(15,23,42,0.18)",
              scaleX: dynamicIslandKeyframes.scaleX,
              scaleY: dynamicIslandKeyframes.scaleY,
              y: dynamicIslandKeyframes.y,
            }}
            transition={dynamicIslandTransition}
            className={`border border-foreground/10 w-full ${innerWidthClass} ${innerMarginClass} rounded-2xl sm:rounded-2xl`}
            style={cardWrapperStyle}
          >
        <motion.div
          ref={collapsedMeasurementRef}
          layout={isClientHydrated}
          initial={false}
          animate={{
            maxHeight: isBottomPlayerExpanded ? expandedMaxHeight : collapsedMaxHeight,
            paddingTop: isBottomPlayerExpanded ? expandedPadding.top : collapsedPadding.top,
            paddingBottom: isBottomPlayerExpanded ? expandedPadding.bottom : collapsedPadding.bottom,
          }}
          className="px-1 sm:px-1.5"
          style={{
            maxHeight: isBottomPlayerExpanded ? expandedMaxHeight : collapsedMaxHeight,
            paddingTop: isBottomPlayerExpanded ? expandedPadding.top : collapsedPadding.top,
            paddingBottom: isBottomPlayerExpanded ? expandedPadding.bottom : collapsedPadding.bottom,
          }}
          transition={dynamicIslandContentTransition}
        >
          {isBottomPlayerExpanded ? expandedPanel : collapsedStrip}
          <div className="flex items-center justify-between">
            <div 
              className={`flex items-center space-x-4 flex-1 min-w-0 ${
                isMainAreaInteractive 
                  ? 'cursor-pointer hover:bg-white/10 rounded-lg p-2 -m-2 transition-all duration-200' 
                  : ''
              }`}
              onClick={isMainAreaInteractive ? handleMainAreaClick : undefined}
            >
              <div className="w-12 h-12 sm:w-11 sm:h-11 rounded-xl overflow-hidden flex-shrink-0 shadow-md">
              {!isClientHydrated ? (
                  <div className="w-full h-full bg-gray-200 rounded-xl flex items-center justify-center">
                    <SpinnerIcon size={24} color="hsl(var(--muted-foreground))" />
                  </div>
                ) : (content?.thumbnail || content?.content?.thumbnail || content?.ownerAvatarUrl || content?.content?.ownerAvatarUrl) ? (
                  <img
                    src={decodeURIComponent(content?.thumbnail || content?.content?.thumbnail || content?.ownerAvatarUrl || content?.content?.ownerAvatarUrl || '')}
                    alt="Episode cover"
                    className="rounded-xl w-full h-full object-cover"
                    onError={(e) => {
                      console.error('Avatar image failed to load:', e.currentTarget.src);
                      // Hide the broken image and show fallback
                      const parent = e.currentTarget.parentElement;
                      if (parent) {
                        parent.innerHTML = '<div class="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 rounded-xl flex items-center justify-center"><svg class="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg></div>';
                      }
                    }}
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 rounded-xl flex items-center justify-center">
                    <UserCircle className="w-6 h-6 text-gray-500" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="overflow-hidden">
                  <div
                    className={`scroll-container ${
                      isPlaying ? "" : "animation-play-state-paused"
                    }`}
                  >
                    <div className="text-sm sm:text-base font-medium text-gray-900 md:truncate pr-4">
                      {isClientHydrated ? content?.content?.title : ''}
                    </div>
                    {isClientHydrated && getTitleTranslationString(content?.content?.titleTranslation) ? (
                      <div className="text-sm sm:text-[14px] font-[400] pt-[3px] text-gray-900 md:truncate pr-4">
                        — {getTitleTranslationString(content?.content?.titleTranslation)}
                      </div>
                    ) : <div className="text-sm sm:text-base font-medium text-gray-900 md:truncate pr-4">
                    {isClientHydrated ? content?.content?.title : ''}
                  </div>}
                  </div>
                </div>
                {!isBottomPlayerExpanded && (
                  <div className="text-xs text-gray-600 mt-0.5">
                    {isClientHydrated ? `${formatTime(youtubeTime)}${!isMobile ? ` / ${formatTime(youtubeDuration)}` : ''}` : '0:00'}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-3 flex-shrink-0 ml-2 sm:ml-4">
              <Button
                variant="ghost"
                size="icon"
                className={`h-12 w-12 sm:h-11 sm:w-11 rounded-xl ${!isMobile ? "hover:bg-white/60" : ""} transition-all duration-200 border border-white/30 ${isBottomPlayerExpanded ? "" : ""}`}
                onClick={(e) => {
                  handleOptimizedPlayPause(e);
                }}
                disabled={isPlayButtonDisabled}
                title={playbackLockTitle}
              >
                {showLoadingSpinner ? (
                  <SpinnerIcon size={28} color="currentColor" />
                ) : isPreviewLocked ? (
                  <Lock className="h-6 w-6 sm:h-5 sm:w-5 text-slate-500" />
                ) : isPlaying ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect
                      x="6"
                      y="4"
                      width="4"
                      height="16"
                      fill="currentColor"
                    />
                    <rect
                      x="14"
                      y="4"
                      width="4"
                      height="16"
                      fill="currentColor"
                    />
                  </svg>
                ) : (
                  <Play
                    fill="currentColor"
                    className="h-6 w-6 sm:h-5 sm:w-5"
                  />
                )}
              </Button>
            </div>

            <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0 ml-2 sm:ml-3">
              <Button
                variant="ghost"
                size="icon"
                className={`h-12 w-12 sm:h-10 sm:w-10 rounded-lg ${!isMobile ? "hover:bg-white/50" : ""} transition-all duration-200 ${isBottomPlayerExpanded ? "" : ""} hidden xs:flex`}
                onClick={onScrollToCurrentTranscript}
                title="Scroll to current transcript"
              >
                <Crosshair className="h-6 w-6 sm:h-5 sm:w-5" />
              </Button>
              <MotionButton
                variant="ghost"
                size="icon"
                layout={isClientHydrated}
                initial={false}
                className={cn(
                  "relative flex items-center justify-center transition-colors",
                  !isMobile && "hover:bg-white/20",
                )}
                style={{
                  width: isBottomPlayerExpanded ? 44 : 44,
                  height: isBottomPlayerExpanded ? 44 : 44,
                }}
                animate={{
                  width: isBottomPlayerExpanded ? 44 : 44,
                  height: isBottomPlayerExpanded ? 44 : 44,
                  borderRadius: isBottomPlayerExpanded ? 30 : 24,
                  backgroundColor: isBottomPlayerExpanded
                    ? "hsl(var(--foreground))"
                    : "rgba(255,255,255,0.08)",
                  color: isBottomPlayerExpanded
                    ? "hsl(var(--background))"
                    : "hsl(var(--foreground))",
                  borderColor: isBottomPlayerExpanded
                    ? "rgba(148,163,184,0.45)"
                    : "rgba(148,163,184,0.25)",
                }}
                transition={{
                  width: { duration: 0.38 },
                  height: { duration: 0.38 },
                  backgroundColor: { duration: 0.4 },
                  borderRadius: { duration: 0.35 },
                  borderColor: { duration: 0.32 },
                  color: { duration: 0.32 },
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  togglePanel();
                }}
                data-prevent-main-click="true"
              >
                <motion.span
                  className="flex h-full w-full items-center justify-center"
                  animate={{
                    rotate: isBottomPlayerExpanded ? 180 : 0,
                    scale: isBottomPlayerExpanded ? 1.05 : 1,
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 180,
                    damping: 24,
                  }}
                >
                  <PanelBottomOpen className="h-6 w-6" />
                </motion.span>
                <span className="sr-only">
                  {isBottomPlayerExpanded
                    ? t("episode.player.collapse", "Close player")
                    : t("episode.player.expand", "Expand player")}
                </span>
              </MotionButton>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`h-12 w-12 sm:h-10 sm:w-10 rounded-lg ${!isMobile ? "hover:bg-white/50" : ""} transition-all duration-200 ${isBottomPlayerExpanded ? "" : ""}`}
                  >
                    <MoreHorizontal className="h-6 w-6 sm:h-5 sm:w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  side="top"
                  className="backdrop-blur-md border rounded-xl p-1 min-w-[120px]"
                >
                  <div className="px-2 py-1 text-xs font-medium border-b border-gray-200 mb-1">
                    Playback Speed
                  </div>
                  {[0.5, 0.75, 1, 1.25, 1.5, 2].map((speed) => (
                    <DropdownMenuItem
                      key={speed}
                      onClick={() => onSetPlaybackSpeed(speed)}
                      className={`flex items-center justify-between px-3 py-1 sm:py-2 text-sm cursor-pointer rounded-lg ${
                        playbackSpeed === speed
                          ? "bg-primary/50 font-medium"
                          : ""
                      }`}
                    >
                      <span>{speed}x</span>
                      {playbackSpeed === speed && (
                        <Check className="h-4 w-4 text-foreground" />
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </motion.div>
          </motion.div>
        </div>
        <AddToPlaylistDialog
          isOpen={showAddToPlaylistDialog}
          onClose={() => setShowAddToPlaylistDialog(false)}
          episodeId={content?.shareId || content?.content?.shareId || ''}
          episodeTitle={content?.content?.title || content?.title || ''}
        />
      </>
    </MotionConfig>
  );
};
